/**
 * @description Optional multi-product picker for New/Edit Recurring Plan (LYN-250).
 * Supports variants and Edit (qty / variant). Amount is computed by the parent
 * from catalog list prices minus evaluate — this field only owns line selection.
 *
 * @dependencies productsService
 * @notes Do not send client subtotalCents as SoT. Server re-prices from catalog.
 */

import { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import productsService from '../../services/api/productsService';
import { formatCurrency } from '../../utils/invoiceHelpers';

function unitPriceCentsFromCatalog(product, variant) {
  if (variant && variant.amountCents != null) {
    return Number(variant.amountCents) || 0;
  }
  if (product.priceCents != null) return Number(product.priceCents) || 0;
  return Math.round(Number(product.price || 0) * 100);
}

export default function RecurringPlanProductsField({ items, onChange }) {
  const [products, setProducts] = useState([]);
  const [variantsByProduct, setVariantsByProduct] = useState({});
  const [search, setSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await productsService.getProducts();
        const list = Array.isArray(result) ? result : (result.products || []);
        if (!cancelled) setProducts(list);
      } catch (err) {
        console.error('Failed to load products', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const loadVariants = async (productId) => {
    if (variantsByProduct[productId]) return variantsByProduct[productId];
    try {
      const result = await productsService.getVariants(productId);
      const list = Array.isArray(result) ? result : (result.variants || []);
      const active = list.filter((row) => row.isActive !== false);
      setVariantsByProduct((prev) => ({ ...prev, [productId]: active }));
      return active;
    } catch (err) {
      console.error('Failed to load variants', err);
      return [];
    }
  };

  const filteredProducts = useMemo(() => {
    const q = search.toLowerCase().trim();
    return products.filter((product) => {
      if (!q) return true;
      return (
        (product.name || '').toLowerCase().includes(q)
        || (product.sku || '').toLowerCase().includes(q)
      );
    }).slice(0, 8);
  }, [products, search]);

  const addProduct = async (product) => {
    const variants = await loadVariants(product.id);
    const firstVariant = variants[0] || null;
    const next = [
      ...items,
      {
        productId: product.id,
        variantId: firstVariant ? firstVariant.id : null,
        variantName: firstVariant ? firstVariant.name : null,
        quantity: 1,
        itemName: firstVariant ? `${product.name} — ${firstVariant.name}` : product.name,
        unitPriceCents: unitPriceCentsFromCatalog(product, firstVariant)
      }
    ];
    onChange(next);
    setSearch('');
    setShowPicker(false);
    if (variants.length > 1) {
      setEditingIndex(next.length - 1);
    }
  };

  const updateLine = (index, patch) => {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange(next);
  };

  const applyVariant = (index, variant, product) => {
    updateLine(index, {
      variantId: variant ? variant.id : null,
      variantName: variant ? variant.name : null,
      itemName: variant ? `${product.name} — ${variant.name}` : product.name,
      unitPriceCents: unitPriceCentsFromCatalog(product, variant)
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm text-gray-600">Products (optional)</label>
        <button
          type="button"
          onClick={() => setShowPicker((open) => !open)}
          className="inline-flex items-center gap-1 text-sm text-brand hover:text-brand-hover"
        >
          <Plus className="w-4 h-4" />
          Add product
        </button>
      </div>

      {showPicker && (
        <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products"
            className="w-full border-b border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <div className="max-h-48 overflow-auto">
            {filteredProducts.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => addProduct(product)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="text-sm font-medium text-gray-900">{product.name}</span>
                <span className="text-sm text-gray-500">
                  {formatCurrency(product.price || 0)}
                </span>
              </button>
            ))}
            {filteredProducts.length === 0 && (
              <p className="px-3 py-4 text-sm text-gray-500">No products found</p>
            )}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item, index) => {
            const product = products.find((row) => row.id === item.productId);
            const variants = variantsByProduct[item.productId] || [];
            const editing = editingIndex === index;
            return (
              <div key={`${item.productId}-${index}`} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.itemName}</p>
                    <p className="text-xs text-gray-500">
                      Qty {item.quantity} · {formatCurrency((item.unitPriceCents || 0) / 100)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={async () => {
                        if (item.productId) await loadVariants(item.productId);
                        setEditingIndex(editing ? null : index);
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-700 rounded"
                      aria-label="Edit line"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange(items.filter((_, i) => i !== index))}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      aria-label="Remove line"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {editing && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={item.quantity}
                        onChange={(e) => updateLine(index, {
                          quantity: Math.max(1, parseInt(e.target.value, 10) || 1)
                        })}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                    </div>
                    {variants.length > 0 && product && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Variant</label>
                        <select
                          value={item.variantId || ''}
                          onChange={(e) => {
                            const variant = variants.find((row) => row.id === e.target.value);
                            applyVariant(index, variant, product);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        >
                          {variants.map((variant) => (
                            <option key={variant.id} value={variant.id}>{variant.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
